import {
  CollectionLockup,
  CollectionLockupContent,
  CollectionLockupTitle,
} from "@opensea/app-kit"
import type { FragmentOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import {
  FlexColumn,
  NumberDisplay,
  SkeletonBlock,
  SkeletonLine,
} from "@opensea/ui-kit"
import { bn } from "@opensea/utils"
import { useTranslations } from "use-intl"
import { ProfileCollectionItemFragment } from "./ProfileCollectionItem.fragment"
import {
  ProfileCollectionListItem as ListItem,
  ProfileCollectionListItemAvatar,
  ProfileCollectionListItemContent,
  ProfileCollectionListItemDescription,
  ProfileCollectionListItemSide,
  ProfileCollectionListItemTitle,
} from "./ProfileCollectionListItem"

export type ProfileCollectionItemProps = {
  collection: FragmentOf<typeof ProfileCollectionItemFragment>
  itemCount: number
}

export const ProfileCollectionItem = ({
  collection: collectionKey,
  itemCount,
}: ProfileCollectionItemProps) => {
  const t = useTranslations("wallet.ProfileCollectionItem")
  const profileCollection = readFragment(
    ProfileCollectionItemFragment,
    collectionKey,
  )

  return (
    <ListItem
      className="mb-0.5 p-2"
      href={{
        pathname: "/profile",
        query: { collectionSlugs: profileCollection.slug },
      }}
      interactive
      key={profileCollection.slug}
    >
      <ProfileCollectionListItemAvatar
        className="inset-shadow-border rounded"
        size={42}
        src={profileCollection.imageUrl ?? ""}
      />
      {/* TODO(jihok): add badge to avatar in ds */}
      <ProfileCollectionListItemContent>
        <CollectionLockup collection={profileCollection}>
          <CollectionLockupContent>
            <CollectionLockupTitle />
          </CollectionLockupContent>
        </CollectionLockup>
        {profileCollection.floorPrice && (
          <ProfileCollectionListItemDescription>
            {t("floor")}&nbsp;
            <NumberDisplay
              suffix={profileCollection.floorPrice.pricePerItem.token.symbol}
              value={profileCollection.floorPrice.pricePerItem.token.unit}
            />
          </ProfileCollectionListItemDescription>
        )}
      </ProfileCollectionListItemContent>
      <ProfileCollectionListItemSide className="items-end">
        <ProfileCollectionListItemTitle>
          <NumberDisplay
            display="usd"
            value={
              profileCollection.topOffer
                ? bn(profileCollection.topOffer.pricePerItem.usd).multipliedBy(
                    itemCount,
                  )
                : undefined
            }
          />
        </ProfileCollectionListItemTitle>
        <ProfileCollectionListItemDescription>
          <NumberDisplay
            suffix={profileCollection.topOffer?.pricePerItem.token.symbol}
            value={
              profileCollection.topOffer
                ? bn(
                    profileCollection.topOffer.pricePerItem.token.unit,
                  ).multipliedBy(itemCount)
                : undefined
            }
          />
        </ProfileCollectionListItemDescription>
      </ProfileCollectionListItemSide>
    </ListItem>
  )
}

export const ProfileCollectionItemSkeleton = () => (
  <ListItem className="px-0">
    <SkeletonBlock className="inset-shadow-border size-12 rounded" />
    <ProfileCollectionListItemContent>
      <FlexColumn className="h-9 gap-2">
        <SkeletonLine className="h-4 w-[120px]" />
        <SkeletonLine className="h-3 w-[100px]" />
      </FlexColumn>
    </ProfileCollectionListItemContent>
    <ProfileCollectionListItemSide>
      <FlexColumn className="h-9 items-end gap-2">
        <SkeletonLine className="h-4 w-[120px]" />
        <SkeletonLine className="h-3 w-[100px]" />
      </FlexColumn>
    </ProfileCollectionListItemSide>
  </ListItem>
)
